import React, { useCallback, useEffect, useState } from "react";
import { ActionHandlerActionCreators } from "../../redux/action-creators";
import Clock from "../Clock";
import { FormattedMessage, useIntl } from "react-intl";
import { LocalizationMessages } from "../../localization/messages";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { MessageBrokerUtils } from "../../utils";
import { Channel, Topic } from "../../constants";
import { SVGIcon2 } from "../common/SVGIconV2";

const Actions = {
  performDepositAction: buttonID =>
    ActionHandlerActionCreators.performDepositAction(buttonID)
};

const openFullscreen = elem => {
  if (
    (document.fullscreenElement !== undefined &&
      document.fullscreenElement === null) ||
    (document.msFullscreenElement !== undefined &&
      document.msFullscreenElement === null) ||
    (document.mozFullScreen !== undefined && !document.mozFullScreen) ||
    (document.webkitIsFullScreen !== undefined && !document.webkitIsFullScreen)
  ) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }
};

const closeFullscreen = () => {
  if (
    (document.fullscreenElement && document.fullscreenElement !== null) ||
    (document.msFullscreenElement && document.msFullscreenElement !== null) ||
    (document.msFullscreenElement && document.mozFullScreen !== null) ||
    (document.msFullscreenElement && document.webkitIsFullScreen !== null)
  ) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }
};

const GameHeader = ({ gameLoading, gameName, icons, appID, correlationID }) => {
  const [isFullscreen, setFullscreen] = useState(false);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const closeHandler = () => {
    setFullscreen(false);
    closeFullscreen();
    MessageBrokerUtils.publish({
      channel: Channel.SESSION,
      topic: Topic.appClosing,
      headers: {
        publisher: "UAA"
      },
      data: { appID: appID, correlationID }
    });
  };

  const handleCashier = buttonID => {
    closeFullscreen();
    setFullscreen(false);
    dispatch(Actions.performDepositAction(buttonID));
  };

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      closeFullscreen();
    } else {
      openFullscreen(document.body);
    }
    setFullscreen(!isFullscreen);
  }, [isFullscreen]);

  useEffect(() => {
    const onWindowResize = () => {
      const maxHeight = window.screen.height,
        maxWidth = window.screen.width,
        curHeight = window.innerHeight,
        curWidth = window.innerWidth;

      if (maxWidth === curWidth && maxHeight === curHeight) {
        if (!isFullscreen) setFullscreen(true);
      } else if (isFullscreen) {
        setFullscreen(false);
      }
    };

    const onKeyDown = e => {
      if (!e.repeat)
        if (e.key === "Escape") {
          closeFullscreen();
          setFullscreen(false);
        } else if (e.key === "F11") {
          e.preventDefault();
          toggleFullscreen();
        }
    };

    window.addEventListener("resize", onWindowResize);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onWindowResize);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullscreen, setFullscreen, toggleFullscreen]);

  return (
    <div className="game-frame-header">
      {gameLoading && <div className="game-frame-overlay" />}
      <div className="game-frame-left-header">
        <div className="game-frame-header-time">
          <Clock />
        </div>
        <div className="game-frame-header-icons">
          {icons.map((icon, id) => (
            <SVGIcon2 key={id} iconName={icon} />
          ))}
        </div>
        <div className={"game-frame-header-title"}>
          <FormattedMessage {...LocalizationMessages.gameFrameTitle} />
          {gameName ? ` ${gameName}` : ""}
        </div>
      </div>
      <div className="game-frame-right-header">
        <div className="game-frame-header-cashier">
          <button onClick={() => handleCashier("DEMO_BUTTON_GAME")}>
            {formatMessage(LocalizationMessages.deposit)}
          </button>
        </div>
        <div
          className="game-frame-header-expand game-frame-header-icon"
          onClick={toggleFullscreen}
        >
          <SVGIcon2
            iconName={isFullscreen ? "game-frame-shrink" : "game-frame-expand"}
          />
        </div>
        <div
          className="game-frame-header-close game-frame-header-icon"
          onClick={closeHandler}
        >
          <SVGIcon2 iconName="game-frame-close" />
        </div>
      </div>
    </div>
  );
};
GameHeader.propTypes = {
  gameLoading: PropTypes.bool,
  gameName: PropTypes.string,
  icons: PropTypes.arrayOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType })
  ]),
  appID: PropTypes.string,
  correlationID: PropTypes.string
};

export default GameHeader;
